import { TemplateResult, LitElement, html } from 'lit';
import { customElement, property, queryAssignedElements } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '../../../controllers/adopted-stylesheets';
import encapsulatedStyles from './styles/outline-accordion-wrapper.encapsulated.css?inline';
import globalStyles from './styles/outline-accordion-wrapper.global.css?inline';
import { OutlineAccordionPanel } from '../outline-accordion-panel/outline-accordion-panel';
import { validateSlot } from '../../../utilities/slot';

const componentName = 'outline-accordion-wrapper';

/**
 * The OutlineAccordionWrapper component
 * @element outline-accordion-wrapper
 * @slot - heading
 * @slot - description
 * @slot - accordion-panels
 */
@customElement('outline-accordion-wrapper')
export class OutlineAccordionWrapper extends LitElement {

    /**
     * Set if only one panel at a time should be open.
     */
    @property({ type: Boolean, attribute: 'single-panel' })
    singlePanel = false;

    /**
     * Set if first panel is open by default.
     */
    @property({ type: Boolean, attribute: 'first-open' })
    firstOpen = false;

    /**
     * Ref to slot for <outline-accordion-panels>
     */
    @queryAssignedElements({ slot: 'accordion-panels' })
    panels: OutlineAccordionPanel[];

    /**
     * Ref to heading slot
     */
    @queryAssignedElements({ slot: 'heading' })
    headingItems: Array<HTMLElement>;

    /**
     * Ref to sub-heading slot
     */
    @queryAssignedElements({ slot: 'description' })
    descriptionItems: Array<HTMLElement>;

    adoptedStyleSheets = new AdoptedStyleSheets(this, {
        globalCSS: globalStyles,
        encapsulatedCSS: encapsulatedStyles,
    });

    firstUpdated() {
        const headingItems: Array<HTMLElement> = [...this.headingItems, ...this.descriptionItems];
        validateSlot(headingItems, '.heading-section', this.shadowRoot);
    }

    /**
     * @param event
     * @returns void
     * Adds up/down arrow keyboard navigation.
     */
    handleKeyboardNav(event: KeyboardEvent) {
        let index = 0;
        const panels = this.panels;
        const start = Array.prototype.indexOf.call(
            panels,
            event.target as HTMLElement
        );

        // bail on wrong key
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
            return;
        }
        if (event.key === 'ArrowDown') {
            if (panels?.length && start + 1 > panels?.length - 1) {
                index = 0;
            } else index = start + 1;
        }
        if (event.key === 'ArrowUp') {
            if (panels?.length && start - 1 < 0) {
                index = panels?.length - 1;
            } else index = start - 1;
        }
        const focusTarget = panels?.[index] as unknown as HTMLElement;
        focusTarget.shadowRoot?.querySelector('button')?.focus();
    }

    render(): TemplateResult {
        return html`
            <div class="heading-section">
                <slot name="heading"></slot>
                <slot name="description"></slot>
            </div>
            <div class="accordion" @keydown=${this.handleKeyboardNav}>
                <slot name="accordion-panels"></slot>
            </div>
        `;
    }

    updated() {
        if (this.singlePanel) {
            this.panels.forEach(panel => {
                if (panel.id === this.id) {
                    panel.setAttribute('active', 'true');
                } else {
                    panel.removeAttribute('active');
                }
            });
        }

        if (this.firstOpen) {
            this.panels[0].setAttribute('active', 'true');
        }
    }
}

/**
 * Declare the component in the global HTMLElementTagNameMap
 */
declare global {
    interface HTMLElementTagNameMap {
        [componentName]: OutlineAccordionWrapper;
    }
}